package com.ijoin.ui.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.calculateEndPadding
import androidx.compose.foundation.layout.calculateStartPadding
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Send
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Divider
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.ChatThread
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.ui.chat.ChatViewModel
import com.ijoin.ui.components.NavigationScaffold
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventStepperButton
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.ui.test.TestTags
import com.ijoin.util.datetime.monthNumberCompat
import kotlinx.datetime.LocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChatScreen(
    initialThread: ChatThread?,
    otherUserId: String?,
    otherUserName: String?,
    onBackClick: () -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ChatViewModel = remember(initialThread?.id, otherUserId) { ChatViewModel(initialThread, otherUserId) },
) {
    val strings = strings()
    val uiState by viewModel.uiState.collectAsState()
    var messageText by remember { mutableStateOf("") }
    val listState = rememberLazyListState()
    val onTabSelected: (String) -> Unit = { tab ->
        when (tab) {
            "home" -> onOpenHome()
            "explore" -> onOpenExplore()
            "create" -> onCreateEventClick()
            "tickets" -> onOpenTickets()
            "profile" -> onOpenProfile()
        }
    }
    val sendCurrentMessage = {
        if (messageText.isNotBlank()) {
            viewModel.sendMessage(messageText)
            messageText = ""
        }
    }
    DisposableEffect(viewModel) {
        onDispose { viewModel.clear() }
    }
    LaunchedEffect(uiState.messages.size) {
        if (uiState.messages.isNotEmpty()) {
            listState.animateScrollToItem(uiState.messages.lastIndex)
        }
    }

    val isGroupChat = uiState.thread?.group == true
    val headerTitle = when {
        isGroupChat -> uiState.thread?.title?.takeIf { it.isNotBlank() } ?: strings.groupChatLabel
        else -> uiState.thread?.otherUserName?.takeIf { it.isNotBlank() }
            ?: otherUserName?.takeIf { it.isNotBlank() }
            ?: strings.messagesTitle
    }

    NavigationScaffold(
        selectedTab = "home",
        onTabSelected = onTabSelected,
        modifier = modifier.testTag(TestTags.ChatScreen.ROOT),
        containerColor = EventCardBackground,
        topBar = {
            TopAppBar(
                title = { Text(text = headerTitle) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = strings.back,
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = EventCardBackground,
                    titleContentColor = EventCardAccentDark,
                    navigationIconContentColor = EventCardAccentDark
                )
            )
        }
    ) { paddingValues ->
        val layoutDirection = LocalLayoutDirection.current
        val focusManager = LocalFocusManager.current
        val imeBottom = WindowInsets.ime.asPaddingValues().calculateBottomPadding()
        val isImeVisible = imeBottom > 0.dp
        val contentPadding = PaddingValues(
            start = paddingValues.calculateStartPadding(layoutDirection),
            top = paddingValues.calculateTopPadding(),
            end = paddingValues.calculateEndPadding(layoutDirection),
            bottom = if (isImeVisible) 0.dp else paddingValues.calculateBottomPadding()
        )
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(eventBackgroundBrush())
                .padding(contentPadding)
                .imePadding()
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                }
        ) {
            if (uiState.isLoading && uiState.messages.isEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator()
                }
            } else {
                LazyColumn(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp)
                        .testTag(TestTags.ChatScreen.MESSAGE_LIST),
                    state = listState,
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    if (uiState.messages.isEmpty()) {
                        item {
                            Text(
                                text = strings.emptyMessages,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(top = 32.dp),
                                textAlign = TextAlign.Center,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    } else {
                        items(uiState.messages) { message ->
                            MessageBubble(
                                content = message.content,
                                isOwn = message.outgoing,
                                timestamp = strings.formatMessageTime(message.createdAt),
                                senderName = message.senderName,
                                showAuthor = isGroupChat && !message.outgoing
                            )
                        }
                    }
                }
            }

            uiState.error?.let { error ->
                Text(
                    text = error.ifBlank { strings.chatLoadError },
                    color = EventCardAlert,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp)
                )
            }

            Divider(color = EventCardOutline)

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
                    .padding(top = 12.dp, bottom = if (isImeVisible) 0.dp else 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                OutlinedTextField(
                    value = messageText,
                    onValueChange = { messageText = it },
                    modifier = Modifier
                        .weight(1f)
                        .heightIn(min = 56.dp)
                        .testTag(TestTags.ChatScreen.MESSAGE_INPUT)
                        .onPreviewKeyEvent { keyEvent ->
                            if (
                                keyEvent.type == KeyEventType.KeyDown &&
                                keyEvent.key == Key.Enter &&
                                !keyEvent.isShiftPressed
                            ) {
                                sendCurrentMessage()
                                true
                            } else {
                                false
                            }
                        },
                    placeholder = { Text(strings.typeMessage) },
                    maxLines = 4,
                    keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Send),
                    keyboardActions = KeyboardActions(onSend = { sendCurrentMessage() }),
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                Spacer(modifier = Modifier.width(12.dp))
                EventStepperButton(
                    onClick = sendCurrentMessage,
                    enabled = messageText.isNotBlank() && !uiState.isSending,
                    icon = Icons.Filled.Send,
                    contentDescription = strings.send,
                    modifier = Modifier.testTag(TestTags.ChatScreen.SEND_BUTTON),
                    size = 40.dp
                )
            }
        }
    }
}

@Composable
private fun MessageBubble(
    content: String,
    isOwn: Boolean,
    timestamp: String,
    senderName: String? = null,
    showAuthor: Boolean = false,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = if (isOwn) Arrangement.End else Arrangement.Start
    ) {
        Column(horizontalAlignment = if (isOwn) Alignment.End else Alignment.Start) {
            if (showAuthor && !senderName.isNullOrBlank()) {
                Text(
                    text = senderName,
                    style = MaterialTheme.typography.labelMedium,
                    color = EventCardAccentDark,
                    modifier = Modifier.padding(bottom = 2.dp, start = 8.dp, end = 8.dp)
                )
            }
            val bubbleColor = if (isOwn) EventCardAccent else EventCardSurface
            val border = if (isOwn) null else BorderStroke(1.dp, EventCardOutline)
            Surface(
                color = bubbleColor,
                shape = RoundedCornerShape(EventCardCompactCorner),
                border = border,
                tonalElevation = if (isOwn) 2.dp else 0.dp
            ) {
                Text(
                    text = content,
                    color = if (isOwn) EventCardSurface else MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.padding(12.dp)
                )
            }
            if (timestamp.isNotEmpty()) {
                Text(
                    text = timestamp,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 4.dp, end = 8.dp, start = 8.dp)
                )
            }
        }
    }
}

private fun Strings.formatMessageTime(timestamp: String?): String {
    if (timestamp.isNullOrBlank()) return ""
    return try {
        val dateTime = LocalDateTime.parse(timestamp)
        val date = dateTime.date
        val time = dateTime.time
        val hour = time.hour.toString().padStart(2, '0')
        val minute = time.minute.toString().padStart(2, '0')
        "${date.dayOfMonth} ${shortMonthName(date.monthNumberCompat())} $hour:$minute"
    } catch (e: Exception) {
        timestamp.replace("T", " ").take(16)
    }
}
